import { FC, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import DataTable from '@/components/functional/DataTable/DataTable.tsx';
import { createColumnHelper } from '@tanstack/react-table';
import supabaseClient from '@/api/supabaseClient.ts';
import { useNavigate } from 'react-router-dom';

interface ReportRow {
  id: number;
  domain_name: string;
  date_completed: string | null;
  quick_view_url: string | null;
  full_summary_url: string | null;
  recommendations_url: string | null;
  isCompleted: boolean;
}

const columnHelper = createColumnHelper<ReportRow>();

const ReportsTablePage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [reportsData, setReportsData] = useState<ReportRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  const getCurrentUserId = async (): Promise<string> => {
    const { data: { user } } = await supabaseClient.auth.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    console.log('Current authenticated user:', user.id);

    const { data: existingUser } = await supabaseClient
      .from('assessment_users')
      .select('id')
      .eq('id', user.id)
      .maybeSingle();

    if (existingUser) {
      console.log('User found in assessment_users:', existingUser.id);
      return existingUser.id;
    }


    const { data: newUser, error: createError } = await supabaseClient
      .from('assessment_users')
      .insert({
        id: user.id,
        email: user.email || '',
        full_name: user.user_metadata?.full_name || user.email || ''
      })
      .select('id')
      .maybeSingle();

    if (createError) {
      console.error('Error creating assessment user:', createError);
      throw createError;
    }

    console.log('Created new assessment user:', newUser?.id);
    return newUser?.id || '';
  };

  const loadReportsData = useCallback(async () => {
    try {
      setLoading(true);
      const userId = await getCurrentUserId();
      setCurrentUserId(userId);

      const { data: domains, error: domainsError } = await supabaseClient
        .from('domains')
        .select('id, name')
        .order('id');

      if (domainsError) {
        console.error('Error fetching domains:', domainsError);
        return;
      }

      const { data: completedAssessments, error: assessmentsError } = await supabaseClient
        .from('user_assessments')
        .select('assessment_name, completed_at, status')
        .eq('user_id', userId)
        .eq('status', 'completed');

      if (assessmentsError) {
        console.error('Error fetching completed assessments:', assessmentsError);
        return;
      }


      const tableData: ReportRow[] = [];

      for (const assessment of completedAssessments || []) {
        const domain = domains?.find(d => d.name === assessment.assessment_name);
        
        if (domain) {
          tableData.push({
            id: domain.id,
            domain_name: domain.name,
            date_completed: assessment.completed_at,
            quick_view_url: 'Quick View',
            full_summary_url: 'Report Summary',
            recommendations_url: 'Recommendations & Action Steps',
            isCompleted: true
          });
        }
      }

      setReportsData(tableData);
    } catch (err) {
      console.error('Error loading reports data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadReportsData();
  }, [loadReportsData]);

  const handleQuickViewClick = async (row: ReportRow) => {
    try {
      if (row.domain_name === 'Summary of All Domains') {

        navigate('/reports/summary');
      } else {

        const { data: assessment, error } = await supabaseClient
          .from('user_assessments')
          .select('id')
          .eq('user_id', currentUserId)
          .eq('assessment_name', row.domain_name)
          .eq('status', 'completed')
          .order('completed_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) {
          console.error('Error finding assessment:', error);
          return;
        }

        if (assessment) {
          navigate(`/assessment-results/${assessment.id}`);
        }
      }
    } catch (err) {
      console.error('Error handling quick view click:', err);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US'); 
  };

  const handleReportSummaryClick = async (row: ReportRow) => {
    // Now shows what was previously under Recommendations & Action Steps
    if (row.domain_name === 'Summary of All Domains') {
      navigate('/reports/domain-recommendations/1?assessmentId=b243594c-31b3-4cfe-ab11-fa1eb87cabc4');
    } else {
      try {
        // Find the assessment ID for this domain
        const { data: assessment, error } = await supabaseClient
          .from('user_assessments')
          .select('id')
          .eq('user_id', currentUserId)
          .eq('assessment_name', row.domain_name)
          .eq('status', 'completed')
          .order('completed_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) {
          console.error('Error finding assessment:', error);
          return;
        }

        if (assessment) {
          navigate(`/reports/domain-recommendations/${row.id}?assessmentId=${assessment.id}`);
        }
      } catch (err) {
        console.error('Error handling report summary click:', err);
      }
    }
  };

  const columns = [
    columnHelper.accessor('domain_name', {
      cell: (info) => <span className='font-semibold'>{info.getValue()}</span>,
      header: () => <span>{t('reports.category')}</span>,
      footer: (info) => info.column.id,
      enableSorting: false,
    }),
    columnHelper.accessor('date_completed', {
      cell: (info) => (
        <span className='text-sm text-gray-900'>
          {formatDate(info.getValue())}
        </span>
      ),
      header: () => <span>{t('reports.date')}</span>,
      footer: (info) => info.column.id,
      enableSorting: false,
    }),
    columnHelper.accessor('quick_view_url', {
      header: () => t('reports.quickView'),
      cell: (info) => (
        <span 
          className='font-bold underline cursor-pointer transition-colors'
          onClick={() => handleQuickViewClick(info.row.original)}
        >
          {t('reports.quickView')}
        </span>
      ),
      footer: (info) => info.column.id,
      enableSorting: false,
    }),
    columnHelper.accessor('full_summary_url', {
      header: () => t('reports.fullSummary'),
      cell: (info) => (
        <span 
          className='font-bold underline cursor-pointer transition-colors'
          onClick={() => handleReportSummaryClick(info.row.original)}
        >
          {t('reports.reportSummary')}
        </span>
      ),
      footer: (info) => info.column.id,
      enableSorting: false,
    }),
  ];

  if (loading) {
    return (
      <div className="flex flex-col gap-4">
        <div className='text-4xl bg-white rounded-2xl px-6 py-4 font-bold shadow-assessment-sm'>
          {t('reports.title')}
        </div>
        <div className="text-center py-8">{t('reports.loading')}</div>
      </div>
    );
  }

  if (reportsData.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        <div className='text-4xl bg-white rounded-2xl px-6 py-4 font-bold shadow-assessment-sm'>
          {t('reports.title')}
        </div>
        <div className="text-center py-8">
          <p className="text-gray-600 mb-4">{t('reports.noCompletedAssessments')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className='text-4xl bg-white rounded-2xl px-6 py-4 font-bold shadow-assessment-sm'>
        {t('reports.title')}
      </div>

      <DataTable 
        data={reportsData} 
        columns={columns} 
        className="[&_tbody_tr:nth-child(odd)]:bg-table-stripe" 
      />
    </div>
  );
};

export default ReportsTablePage;